import { Card } from 'modules/shared-components/card';
import { useState } from 'react';
import {
  ChartBarIcon,
  FireIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { GetDexOpenPositionsParams } from 'api/types/httpsTypes/dex-trade';
import usePositionsState, {
  positionStatesStatuses,
} from '../trade-monitor/usePageState';
import { SnipesPanel } from './SnipesPanel';
import { WalletsPanel } from './WalletsPanel';
import Button from 'modules/shared-components/button/button';
import { useToggle } from 'modules/utils/useToggle';
import { SnipesFilters } from './SnipesFilters';
import { DexPanelButton } from '../components/tabs';
import { useSelectedChain } from '../components/chains/getChains';
import { useUserState } from 'modules/user/UserContext';
import PageTemplate from '../components/layout';
import { Link } from 'react-router-dom';

export type SnipePanelTab = 'history' | 'active-snipes' | 'bots';

export function LeftSnipeSidePanel() {
  const [activeTab, setActiveTab] = useState<SnipePanelTab>('active-snipes');
  const [isOpenFilters, { open: openFilters, close: closeFilters }] =
    useToggle();
  const { setFilters } = usePositionsState();
  return (
    <div className="xxs:flex xxs:flex-col space-y-1  h-0 min-h-full">
      <Card className="xxs:p-1 space-y-1 xxs:bg-dex-black-800 overflow-hidden max-h-[200px]">
        <WalletsPanel />
      </Card>

      <Card className="xxs:p-1 space-y-1 xxs:bg-dex-black-800 overflow-hidden">
        <div className="grid grid-flow-col xxs:gap-1">
          <DexPanelButton
            onClick={() => {
              setFilters({});
              setActiveTab('active-snipes');
            }}
            isActive={activeTab === 'active-snipes'}
          >
            <div className="flex space-x-1 items-center justify-center">
              <FireIcon className="h-4 w-4" />
              <div>Trades</div>
            </div>
          </DexPanelButton>
          <DexPanelButton
            onClick={() => {
              setFilters({});
              setActiveTab('history');
            }}
            isActive={activeTab === 'history'}
          >
            <div className="flex space-x-1 items-center justify-center">
              <ChartBarIcon className="h-4 w-4" />
              <div> History</div>
            </div>
          </DexPanelButton>
          <Button
            onClick={openFilters}
            variant="dexNeutral"
            className="xxs:p-1"
          >
            <FunnelIcon className="xxs:w-4 xxs:h-4" />
          </Button>
        </div>
        {activeTab === 'active-snipes' && <ActiveSnipesPanel />}
        {activeTab === 'history' && <HistoryPanel />}
        {isOpenFilters && <SnipesFilters close={closeFilters} />}
      </Card>
    </div>
  );
}

export function ActiveSnipesPanel() {
  const selectedChain = useSelectedChain();
  const { filtersState: filter } = usePositionsState();

  const { user } = useUserState();

  if (!user) {
    return (
      <div>
        <div className="lg:hidden">
          <PageTemplate>
            <div className="flex flex-col justify-center items-center py-24 xxs:mx-4 space-y-2">
              <div className="text-sm font-bold">
                Login or signup to manage your snipes.
              </div>
              <Button
                variant="primary"
                as={Link}
                to="/login"
                className=" xxs:px-12 "
                type="button"
              >
                LOGIN
              </Button>
            </div>
          </PageTemplate>
        </div>
        <div className="hidden lg:block">
          <div className="divide-x-0 divide-solid dark:divide-black-700 xxs:divide-white-700 xxs:h-[600px] lg:h-auto divide-y overflow-y-auto no-scrollbar overflow-y-scroll">
            <Link
              className="text-blue-500 hover:text-blue-600 xxs:px-2 font-bold xxs:text-xs xxs:pt-1"
              to="/login"
            >
              Login or sign-up
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const params: GetDexOpenPositionsParams = {
    sort: `-updatedAt`,
    chain: selectedChain,
    status: positionStatesStatuses.active,
    ...(filter.botId ? { botId: filter.botId } : {}),
    ...(filter.tokenAddress ? { tokenAddress: filter.tokenAddress } : {}),
  };

  return <SnipesPanel params={params} />;
}

export function HistoryPanel() {
  const { filtersState } = usePositionsState();
  const selectedChain = useSelectedChain();
  const { user } = useUserState();
  if (!user) {
    return (
      <div>
        <div className="lg:hidden">
          <PageTemplate>
            <div className="flex flex-col justify-center items-center py-24 xxs:mx-4 space-y-2">
              <div className="text-sm font-bold">
                Login or signup to manage your snipes.
              </div>
              <Button
                variant="primary"
                as={Link}
                to="/login"
                className=" xxs:px-12 "
                type="button"
              >
                LOGIN
              </Button>
            </div>
          </PageTemplate>
        </div>
        <div className="hidden lg:block">
          <div className="divide-x-0 divide-solid dark:divide-black-700 xxs:divide-white-700 xxs:h-[600px] lg:h-auto divide-y overflow-y-auto no-scrollbar overflow-y-scroll">
            <Link
              className="text-blue-500 hover:text-blue-600 xxs:px-2 font-bold xxs:text-xs xxs:pt-1"
              to="/login"
            >
              Login or sign-up
            </Link>
          </div>
        </div>
      </div>
    );
  }
  const params: GetDexOpenPositionsParams = {
    sort: `-updatedAt`,
    chain: selectedChain,
    status: positionStatesStatuses.history,
    ...(filtersState.botId ? { botId: filtersState.botId } : {}),
    ...(filtersState.tokenAddress
      ? { tokenAddress: filtersState.tokenAddress }
      : {}),
  };
  return <SnipesPanel params={params} />;
}
